import React from 'react'
import { Box, Skeleton, styled } from '@mui/material'
import { Chip } from '@/design-system'
import { APP_NAME } from '@/utils/constants'

const Card = styled(Box)({
  background: 'var(--ds-canvas)',
  borderRadius: 'var(--ds-radius-lg)',
  boxShadow: 'var(--ds-shadow-soft)',
  padding: '28px 30px',
})

const Quote = styled(Box)({
  fontSize: 18,
  lineHeight: 1.6,
  fontStyle: 'italic',
  color: 'var(--ds-ink)',
  borderLeft: '2.5px solid transparent',
  borderImage: 'var(--ds-gradient-spectrum) 1',
  paddingLeft: 18,
})

const Row = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginTop: 18,
  fontSize: 13,
  color: 'var(--ds-ink-muted)',
})

const CircleTags = styled(Box)({
  display: 'flex',
  gap: 8,
  marginTop: 18,
  flexWrap: 'wrap',
})

export interface ProfileBioCardProps {
  motto?: string
  profession?: string
  location?: string
  joinDate?: string
  mutualCount?: number
  mutualNames?: string[]
  circleTags?: string[]
  loading?: boolean
}

function formatJoinDate(raw?: string) {
  if (!raw) return undefined
  const d = new Date(raw)
  if (isNaN(d.getTime())) return undefined
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

export function ProfileBioCard({
  motto,
  profession,
  location,
  joinDate,
  mutualCount,
  mutualNames,
  circleTags,
  loading,
}: ProfileBioCardProps) {
  if (loading) {
    return (
      <Card>
        <Skeleton height={18} sx={{ mb: 1 }} />
        <Skeleton width='80%' height={18} sx={{ mb: 2.5 }} />
        <Skeleton width='60%' height={14} sx={{ mb: 1 }} />
        <Skeleton width='50%' height={14} />
      </Card>
    )
  }

  const joined = formatJoinDate(joinDate)
  const workLine = profession ? (
    <>
      💼&nbsp; Works as {/^[aeiou]/i.test(profession) ? 'an' : 'a'} <b style={{ color: 'var(--ds-ink)' }}>{profession}</b>
      {location ? <> in {location}</> : null}
    </>
  ) : location ? (
    <>📍&nbsp; Based in <b style={{ color: 'var(--ds-ink)' }}>{location}</b></>
  ) : null

  const mutualLine = mutualCount ? (
    <>
      ◎&nbsp; <b style={{ color: 'var(--ds-ink)' }}>{mutualCount} mutual connection{mutualCount === 1 ? '' : 's'}</b>
      {mutualNames && mutualNames.length > 0 ? <>, including {mutualNames.slice(0, 2).join(' and ')}</> : null}
    </>
  ) : null

  return (
    <Card>
      {motto && <Quote>"{motto}"</Quote>}
      {joined && <Row>🗓&nbsp; Joined {APP_NAME} in <b style={{ color: 'var(--ds-ink)', marginLeft: 4 }}>{joined}</b></Row>}
      {workLine && <Row>{workLine}</Row>}
      {mutualLine && <Row>{mutualLine}</Row>}
      {circleTags && circleTags.length > 0 && (
        <CircleTags>
          {circleTags.map((tag) => (
            <Chip key={tag} tone='live' sx={{ pointerEvents: 'none' }}>{tag}</Chip>
          ))}
        </CircleTags>
      )}
    </Card>
  )
}
